import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import _ from 'lodash';

import { format } from '@breezeway/brzw-react-core';

import {
  CALENDAR_SINGLE_TASK,
  CALENDAR_WORKFLOW_GROUP,
  CALENDAR_THEMES,
  SCHEDULE_CELL_TYPE,
  scheduleCellTypes,
  SCHEDULE_SUBJECT,
} from '../../constants';
import {
  SCHEDULE_ROUTE_DATE_FORMAT,
  SCHEDULE_ROUTE_TIME_FORMAT,
} from '../../../../schedule/constants';

import './cell.scss';

import { FLYOUT_TYPES, addFlyout } from '../../../utils/flyout';

const MIN_TIME = '00:00';
const SUBJECT_UNASSIGNED = 'unassigned';

export class Cell extends PureComponent {
  constructor(props) {
    super(props);

    this.CLASS_NAME = 'brzw-calendar-cell';
    this.MAX_CHILDREN_WHEN_CURLED = 3;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.extendElementProps = this.extendElementProps.bind(this);
    this.renderChildren = this.renderChildren.bind(this);
    this.renderOverflowTile = this.renderOverflowTile.bind(this);
    this.renderTrigger = this.renderTrigger.bind(this);
    this.renderHeaderContent = this.renderHeaderContent.bind(this);
    this.renderCellContent = this.renderCellContent.bind(this);
  }

  onTriggerClick() {
    const { cellDate, subjectId, subjectType } = this.props;
    const { formatDate } = format.datetime;
    const time = cellDate ? formatDate(cellDate, null, SCHEDULE_ROUTE_TIME_FORMAT) : null;

    const props = {
      date: cellDate ? formatDate(cellDate, null, SCHEDULE_ROUTE_DATE_FORMAT) : null,
    };

    if (time !== MIN_TIME && !_.isEmpty(time)) {
      _.set(props, 'time', time);
    }

    if (_.isEqual(subjectType, SCHEDULE_SUBJECT.PROPERTY)) {
      props.propertyId = subjectId;
    } else if (subjectId !== SUBJECT_UNASSIGNED) {
      props.personId = subjectId;
    }

    addFlyout(FLYOUT_TYPES.TASK_CREATE, props);
  }

  extendElementProps(element) {
    const { disablePropagation, expandedRow } = this.props;

    if (React.isValidElement(element) && !disablePropagation) {
      return React.cloneElement(element, {
        expanded: expandedRow,
      });
    }

    return element;
  }

  renderChildren() {
    const { children, expandedRow, noTaskLimit, type } = this.props;

    let filteredSingleTasks = 0;

    const mappedChildren = React.Children.map(children, child => {
      const filteredGrandchild = [];
      let shouldReturnChild = true;
      let shouldReturnGrandchild = false;

      if (!noTaskLimit && !expandedRow && type === SCHEDULE_CELL_TYPE.DATA) {
        const componentName = _.get(child, 'props.componentName', '');

        if (componentName === CALENDAR_SINGLE_TASK) {
          const isEmpty = _.get(child, 'props.empty', false);

          if (isEmpty) {
            shouldReturnChild = false;
          } else {
            if (filteredSingleTasks >= this.MAX_CHILDREN_WHEN_CURLED) {
              shouldReturnChild = false;
            }

            filteredSingleTasks += 1;
          }
        } else if (componentName === CALENDAR_WORKFLOW_GROUP) {
          shouldReturnChild = false;

          const grandchildrens = _.get(child, 'props.children');

          _.forEach(grandchildrens, grandchild => {
            const isEmpty = _.get(grandchild, 'props.empty', false);

            if (!isEmpty) {
              if (filteredSingleTasks < this.MAX_CHILDREN_WHEN_CURLED) {
                shouldReturnGrandchild = true;

                filteredGrandchild.push(grandchild);
              }

              filteredSingleTasks += 1;
            }
          });
        }
      }

      if (shouldReturnChild) {
        return this.extendElementProps(child);
      } else if (shouldReturnGrandchild) {
        return React.Children.map(filteredGrandchild, grandchild =>
          this.extendElementProps(grandchild),
        );
      }

      return null;
    });

    const overflowSingleTasks = filteredSingleTasks - this.MAX_CHILDREN_WHEN_CURLED;

    if (!expandedRow && overflowSingleTasks > 0) {
      return [...mappedChildren, this.renderOverflowTile(overflowSingleTasks)];
    }

    return mappedChildren;
  }

  renderOverflowTile(overflowSingleTasks) {
    return (
      <button
        className={`${this.CLASS_NAME}__overflow-tile brzw-button`}
        key="brzw-calendar-cell-overflow-tile"
        onClick={this.props.expandRow}
        type="button"
      >
        +{overflowSingleTasks}
      </button>
    );
  }

  renderTrigger() {
    const { disableTrigger, expandedRow } = this.props;

    const triggerClassName = classnames(`${this.CLASS_NAME}__trigger`, {
      [`${this.CLASS_NAME}__trigger--curled`]: !expandedRow,
      'icon-plus': !disableTrigger,
    });

    return disableTrigger ? (
      <div className={triggerClassName} />
    ) : (
      <Button className={triggerClassName} color="transparent" onClick={this.onTriggerClick} />
    );
  }

  renderHeaderContent() {
    const { expandedRow, firstDataInRow, headerContent, rowHasReservations, type } = this.props;

    if (type !== SCHEDULE_CELL_TYPE.DATA || (!headerContent && !rowHasReservations)) {
      return null;
    }

    const mappedHeaderChildren = React.Children.map(headerContent, child => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          expanded: expandedRow,
          firstDataInRow,
        });
      }

      return child;
    });

    return <div className={`${this.CLASS_NAME}__header-wrapper`}>{mappedHeaderChildren}</div>;
  }

  renderCellContent() {
    const { expandedRow, type } = this.props;

    return (
      <Fragment>
        {this.renderHeaderContent()}

        {type === SCHEDULE_CELL_TYPE.DATA && (
          <div className={`${this.CLASS_NAME}__children-wrapper`}>
            {this.renderChildren()}
            {!expandedRow && this.renderTrigger()}
          </div>
        )}

        {type !== SCHEDULE_CELL_TYPE.DATA && this.renderChildren()}

        {expandedRow && this.renderTrigger()}
      </Fragment>
    );
  }

  render() {
    const {
      activeCell,
      centered,
      clickable,
      compact,
      containerClassName: propsContainerClassName,
      disableTrigger,
      expandedRow,
      extensive,
      firstInRow,
      heading,
      hightlight,
      isAfterTodayCell,
      isBeforeToday,
      isEmpty,
      lastInRow,
      onCellClick,
      theme,
      zIndex,
    } = this.props;

    const containerClassName = classnames(this.CLASS_NAME, propsContainerClassName, {
      [`${this.CLASS_NAME}--active-cell`]: activeCell,
      [`${this.CLASS_NAME}--cell-in-past`]: isBeforeToday,
      [`${this.CLASS_NAME}--cell-in-future`]: isAfterTodayCell,
      [`${this.CLASS_NAME}--centered`]: centered,
      [`${this.CLASS_NAME}--clickable`]: clickable,
      [`${this.CLASS_NAME}--compact`]: compact,
      [`${this.CLASS_NAME}--curled`]: !expandedRow,
      [`${this.CLASS_NAME}--extensive`]: extensive,
      [`${this.CLASS_NAME}--first-in-row`]: firstInRow,
      [`${this.CLASS_NAME}--heading`]: heading,
      [`${this.CLASS_NAME}--hightlighted`]: hightlight,
      [`${this.CLASS_NAME}--is-empty`]: isEmpty,
      [`${this.CLASS_NAME}--last-in-row`]: lastInRow,
      [`${this.CLASS_NAME}--with-trigger`]: !disableTrigger,
      [`${this.CLASS_NAME}--theme-${theme}`]: theme,
    });

    if (clickable) {
      return (
        <div
          className={containerClassName}
          onClick={onCellClick}
          role="presentation"
          style={{ zIndex }}
        >
          {this.renderCellContent()}
        </div>
      );
    }

    return (
      <div className={containerClassName} style={{ zIndex }}>
        {this.renderCellContent()}
      </div>
    );
  }
}

Cell.propTypes = {
  activeCell: PropTypes.bool,
  cellDate: PropTypes.shape({}),
  centered: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  clickable: PropTypes.bool,
  compact: PropTypes.bool,
  containerClassName: PropTypes.string,
  disablePropagation: PropTypes.bool,
  disableTrigger: PropTypes.bool,
  expandedRow: PropTypes.bool,
  expandRow: PropTypes.func,
  extensive: PropTypes.bool,
  firstDataInRow: PropTypes.bool,
  firstInRow: PropTypes.bool,
  headerContent: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  heading: PropTypes.bool,
  hightlight: PropTypes.bool,
  isAfterTodayCell: PropTypes.bool,
  isBeforeToday: PropTypes.bool,
  isEmpty: PropTypes.bool,
  lastInRow: PropTypes.bool,
  noTaskLimit: PropTypes.bool,
  onCellClick: PropTypes.func,
  rowHasReservations: PropTypes.bool,
  subjectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subjectType: PropTypes.string,
  theme: PropTypes.oneOf(_.values(CALENDAR_THEMES)),
  type: PropTypes.oneOf(scheduleCellTypes),
  zIndex: PropTypes.number,
};

Cell.defaultProps = {
  activeCell: false,
  centered: false,
  clickable: false,
  compact: false,
  containerClassName: '',
  disablePropagation: false,
  disableTrigger: false,
  expandedRow: false,
  expandRow: () => {},
  extensive: false,
  firstDataInRow: false,
  firstInRow: false,
  headerContent: null,
  heading: false,
  hightlight: false,
  isBeforeToday: false,
  isEmpty: false,
  lastInRow: false,
  noTaskLimit: false,
  onCellClick: () => {},
  rowHasReservations: false,
  subjectId: null,
  theme: CALENDAR_THEMES.PRIMARY,
  type: SCHEDULE_CELL_TYPE.DATA,
  zIndex: 1,
};

export default Cell;
